import Parse from 'parse';

import { Affiliate, Organization as ParseOrganization, User } from '../api';
import { Asset } from '../api/asset/asset.model';
import { Case } from '../api/case/case.model';
import { AssetStateName } from '../api/data/asset-state-name';
import { StateName } from '../api/data/workflow-state';
import { Report } from '../api/report/report.model';
import { Sample } from '../api/sample/sample.model';
import { IInviteUserData } from './models';

import { AffiliateExportRow, ILicenseInfo, Organization, StepTask } from '@telespot/domain';

/**
 * Class containing static wrapper functions for Telespot cloud functions
 *
 * @export
 * @class CloudFunctions
 */
export class CloudFunctions {
  /**
   * Sets the state of a specific object
   *
   * @static
   * @param {(Asset | Case | Report)} item
   * @param {(AssetStateName | StateName)} state
   * @returns
   * @memberof CloudFunctions
   */

  static async delete({ className, id }) {
    await Parse.Cloud.run('delete', {
      entity: className,
      id: id,
    });
  }
  static async copyFindingsFromSample(
    sampleId: string,
    currUserId: string,
    createdBy: Parse.Pointer
  ): Promise<{
    findingsInfo: {
      uuid: string;
      version: number;
      assetId: string;
      type: StepTask;
    }[];
    enableMosaicView: boolean;
  }> {
    return await Parse.Cloud.run('copyFindings', { sampleId, currUserId, createdBy });
  }

  static async checkIfMosaicIsEnabled(sampleId: string): Promise<{
    enableMosaicView: boolean;
  }> {
    return await Parse.Cloud.run('checkIfMosaic', { sampleId });
  }

  static async moveCases(workspaceId: string, caseId: string) {
    return await Parse.Cloud.run('moveCases', { workspaceId, caseId });
  }

  static async copyCases(workspaceId: string, caseId: string) {
    return await Parse.Cloud.run('copyCases', { workspaceId, caseId });
  }

  static async decryptCases(cases: string[], namesOnly: boolean) {
    return await Parse.Cloud.run('decryptCases', {
      cases,
      namesOnly,
    });
  }

  static async saveCaseData(caseId: string, data: unknown, name?: string) {
    return await Parse.Cloud.run('saveCaseData', {
      caseId,
      data,
      name,
    });
  }

  static async saveAssetMetadata(assetId: string, data: unknown) {
    return await Parse.Cloud.run('saveAssetMetadata', {
      assetId,
      data,
    });
  }

  static GetRefStripData(requestRefStripData) {
    return Parse.Cloud.run('getRefStripData', {
      sampleId: requestRefStripData.sampleId,
      createdBy: requestRefStripData.createdBy,
      itemsToFetch: requestRefStripData.itemsToFetch,
    });
  }
  static GetSampleAnalsysStats(sample: Sample, createdBy: Parse.Pointer, excludeRois = false, filterRois = false) {
    return Parse.Cloud.run('getSampleAnalsysStats', {
      sampleId: sample.id,
      createdBy: createdBy,
      excludeRois,
      filterRois,
    });
  }
  static SetState(item: Asset | Case | Report | Sample, state: AssetStateName | StateName, user: User) {
    return Parse.Cloud.run('setState', {
      object: {
        id: item.id,
        className: item.className,
      },
      state,
      user: user.toPointer(),
    });
  }

  static SendResetPasswordEmail(email: string) {
    return Parse.Cloud.run('sendResetPasswordEmail', {
      email,
    });
  }

  static SendVerificationEmail(email: string) {
    return Parse.Cloud.run('sendVerificationEmail', {
      email,
    });
  }

  static ResetPassword(id: string, token: string, password: string) {
    return Parse.Cloud.run('resetPassword', {
      id,
      token,
      password,
    });
  }

  static VerifyUser(id: string, token: string, password: string, username: string) {
    return Parse.Cloud.run('verifyUser', {
      id,
      token,
      password,
      username,
    });
  }

  static PrioritizeAI(sample: Sample, { workflow }: { workflow?: string } = {}) {
    return Parse.Cloud.run('analyseSample', {
      ...{ sample: sample.toPointer() },
      ...(workflow ? { workflow: { objectId: workflow, className: 'Workflow' } } : {}),
    });
  }

  static SetLicense(organizationId: string, license: ILicenseInfo, force = true): Promise<Organization> {
    return Parse.Cloud.run('setLicense', {
      organizationId: organizationId,
      license,
      force,
    });
  }

  static InviteUser({ user, organization, inviteCode }: IInviteUserData): Promise<Affiliate>;
  static InviteUser(user: User | IInviteUserData, organization?: ParseOrganization): Promise<Affiliate>;
  static InviteUser(data: User | IInviteUserData, organization?: ParseOrganization): Promise<Affiliate> {
    const org = (data as IInviteUserData)?.organization ?? organization;
    const user = (data as IInviteUserData)?.user ?? (data as User);
    const inviteCode = (data as IInviteUserData)?.inviteCode ?? undefined;
    return Parse.Cloud.run('inviteUser', {
      user: user.isNew() ? user.toJSON() : user.toPointer(),
      organization: org?.toPointer(),
      inviteCode,
    });
  }

  static RedeemInvite({ user, inviteCode }: Pick<IInviteUserData, 'user' | 'inviteCode'>): Promise<Affiliate> {
    return Parse.Cloud.run('redeemInvite', {
      user: user?.toPointer(),
      inviteCode,
    });
  }

  static AddUser({
    emailOrUsername,
    organizationId,
    password,
  }: {
    emailOrUsername: string;
    organizationId: string;
    password: string;
  }) {
    return Parse.Cloud.run('addUser', {
      emailOrUsername,
      organizationId,
      password,
    });
  }

  static ExportOrganizationAffiliates(organizationId: string): Promise<AffiliateExportRow[]> {
    return Parse.Cloud.run('exportOrganizationAffiliates', { organizationId });
  }

  static ListLabels({
    language,
    searchTerm,
    limit,
    pagination,
  }: {
    language: 'en' | 'es' | 'pt' | 'fr' | 'it';
    searchTerm?: string;
    limit?: number;
    pagination?: { lastItemName: string; lastItemUUID: string };
  }) {
    return Parse.Cloud.run('listLabels', {
      language,
      searchTerm,
      limit,
      pagination,
    });
  }

  static CountLabels({ searchTerm }: { searchTerm?: string }): Promise<number> {
    return Parse.Cloud.run('countLabels', { searchTerm });
  }

  static StitchSample({ sampleId, tag }: { sampleId: string; tag: string }) {
    return Parse.Cloud.run('stitchSample', {
      sampleId,
      tag,
    });
  }

  static ListAlgorithmUsage({ algorithmId, batchSize }: { algorithmId: string; batchSize: number }) {
    return Parse.Cloud.run('listAlgorithmUsage', {
      algorithmId,
      batchSize,
    });
  }

  /**
   * Lists the method types using a label, with the colour the label carries on
   * each of them. Admin-only, and server-side because the underlying read needs
   * the master key. Callers own the row shape.
   */
  static ListLabelUsage({ labelUuid }: { labelUuid: string }) {
    return Parse.Cloud.run('listLabelUsage', { labelUuid });
  }

  /**
   * Returns a short-lived signed URL to download the eIFU (Instructions For Use)
   */
  static GetIfuUrl({
    variant,
    lang,
  }: {
    variant: 'ce' | 'legacy';
    lang: 'en' | 'es' | 'pt' | 'fr' | 'it';
  }): Promise<{ url: string }> {
    return Parse.Cloud.run('getIfuUrl', { variant, lang });
  }
}
